// =========================================================
// * Vue Material Dashboard PRO - v1.3.1
// =========================================================
//
// * Product Page: https://www.creative-tim.com/product/vue-material-dashboard-pro
// * Copyright 2019 Creative Tim (https://www.creative-tim.com)
//
// * Coded by Creative Tim
//
// =========================================================
//
// * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

import Vue from "vue";
import VueRouter from "vue-router";
import DashboardPlugin from "./material-dashboard";
import * as queries from "@/graphql/queries";
import * as queries_custom from "@/graphql/queries_custom";

import appSyncConfig from './aws-exports'

import VueApollo from 'vue-apollo'
import ApolloClient from 'apollo-client';

import { ApolloLink } from 'apollo-link';
import { WebSocketLink } from 'apollo-link-ws';
import { createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

import { InMemoryCache } from 'apollo-cache-inmemory';

// Plugins
import App from "./App.vue";

// plugin setup
Vue.use(VueRouter);
Vue.use(DashboardPlugin);

import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

Vue.use(require('vue-moment'));
//TODO: We should solve for this another way.  Global mixin is not ideal.
import titleMixin from './mixins/pageTitleMixin';
Vue.mixin(titleMixin);

// Import the Charting library, then make it available globally
import Chartist from "chartist";
Object.defineProperty(Vue.prototype, "$Chartist", {
  get() {
    return this.$root.Chartist;
  }
});

// Make a session object available globally
Object.defineProperty(Vue.prototype, "$Session", {
  get() {
    return this.$root.Session;
  },
  set(val) {
    this.$root.Session = val;
  }
});

// A Way to slice out nested objects based on a value inside
Object.filter = (obj, predicate) => 
  Object.fromEntries(Object.entries(obj).filter(predicate));

// A way to include custom functions globally inside the $u object
import utils from "./utils";
Vue.prototype.$u = utils;
Vue.use(utils);

// Fixed the console and prevents logging output if debugMode is false
import $c from "@/plugins/console";
// set debugMode to true if you want console.logging enabled
Vue.use($c, { debugMode: true });

// for loop wrapper
import $loopo from "@/plugins/loopo";
Vue.use($loopo);

// object reducer
import $reduce from "@/plugins/reduce";
Vue.use($reduce);

// formatter object
import $format from "@/plugins/format";
Vue.use($format);

// @see https://www.npmjs.com/package/vue-linkify
import linkify from 'vue-linkify'
Vue.directive('linkified', linkify)

// @see https://portal-vue.linusb.org/guide/getting-started.html#what-is-portalvue
import PortalVue from 'portal-vue'
Vue.use(PortalVue)

// Amplify libraries
import Amplify, * as AmplifyModules from "aws-amplify";
import { AmplifyPlugin } from "aws-amplify-vue";
import aws_exports from "./aws-exports";
Amplify.configure(aws_exports);
Vue.use(AmplifyPlugin, AmplifyModules);

//Cutom filter
Vue.filter('kb', val => {
  return Math.floor(val/1024);  
});

const url = appSyncConfig.aws_appsync_graphqlEndpoint;
const urlRealtime = appSyncConfig.aws_appsync_graphqlEndpoint
  .replace('https:', 'wss://')
  .replace('.appsync-api.', '.appsync-realtime-api.');
const region = appSyncConfig.aws_appsync_region;
const auth = {
  type: appSyncConfig.aws_appsync_authenticationType,
  jwtToken: async () => {
    const session = await Amplify.Auth.currentSession();;
    return session.getIdToken().getJwtToken();
  }
};

const wsLink = new WebSocketLink({
  uri: urlRealtime,
  options: {
    reconnect: true,
    subprotocols: ['graphql-ws'],
  },
})

const link = ApolloLink.from([
  createAuthLink({ url, region, auth }),
  createSubscriptionHandshakeLink({ url, wsLink, auth })
]);

const client = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
})

const appsyncProvider = new VueApollo({
  defaultClient: client
});

Vue.use(VueApollo)

// router setup
import routes from "./routes/routes";
// configure router
const router = new VueRouter({
  routes, // short for routes: routes
  linkExactActiveClass: "nav-item active",
  // ATL-87 : commented out by requested
  /*scrollBehavior: to => {
    if (to.hash) {
      return { selector: to.hash };
    } else {
      return { x: 0, y: 0 };
    }
  }*/
});
/* eslint-disable no-new */
let vm = new Vue({
  provide: appsyncProvider.provide(),
  render: h => h(App),
  router,
  data() {
    return {
      Chartist: Chartist,
      Session: {
        user: null,
        groups: [],
        alerts: [],
        userName: "Unauthenticated",
        preferences: {
          sidebarBackgroundColor: "azure",
          sidebarBackground: "black",
          sidebarBackgroundImage: "./img/sidebar-2.jpg",
          sidebarMini: true,
          sidebarImg: true
        }}
    };
  },
  beforeCreate() {
    this.$c.log("main.js beforeCreate() starting.");

    router.beforeResolve((to, from, next) => {
      let searchOverlay = window.document.getElementById('mk-search-overlay');
      if (searchOverlay) searchOverlay.classList.remove("mk-fullscreen-search-overlay-show");

      if (to.matched.some(record => record.meta.requiresAuth)) {
        this.$c.log("main.js router.beforeResolve auth requirement identified, checking session...");

        this.$c.log("main.js router.beforeResolve this.$Amplify.Auth.currentAuthenticatedUser()");
        this.$Amplify.Auth.currentAuthenticatedUser()
          .then(data => {
            if (data && data.signInUserSession) {
              this.$set(this.$Session, 'user', data);
              this.$set(this.$Session, 'deepLinkHistory', []);
              this.$set(this.$Session, 'isInternalGroupMember', this.isInternalGroupMember());
              this.getCurrentUser(this.$Session.user.username, 'searchAtlasUsers').then(result => {
                this.$set(this.$Session, 'userId', result.id);
                this.$Session.alerts = result.alerts.items;
              }).catch(error => {
                  this.$c.log("error while getting user", error);
              });
              this.$c.log("main.js router.beforeResolve session good.", this.$Session.user);
              this.getUserTeamList(this.$Session.user.username, 'searchTeamUsers').then(result => {
                this.handleTeamListResponse(result);
              }).catch(error => {
                this.handleTeamListError(error);
              });              
            }
            next();
          })
          .catch(e => {
            this.$c.log(e);
            next({
              path: "/login",
              query: {
                redirect: to.fullPath
              }
            });
          });
      }
      next();
    });

    // Right here we are going to load the user data
    // :Currently makes a request to Cognito
    // :vm.$mount was moved inside the success or failure of this request so that we can wait to render the templates until we know if the user is logged in.
    // :we may need to reconsider this approach as the UI will not load until success/failure of this request.
    // :i would think there can be a way of forcing a partial/full re-render after request is made.
    this.$c.log("main.js beforeCreate this.$Amplify.Auth.currentAuthenticatedUser()");
    this.$Amplify.Auth.currentAuthenticatedUser()
      .then(async data => {
        if (data && data.signInUserSession) {
          this.$set(this.$Session, 'user', data);
          this.$set(this.$Session, 'deepLinkHistory', []);
          this.$set(this.$Session, 'isInternalGroupMember', this.isInternalGroupMember());
          await this.getCurrentUser(this.$Session.user.username, 'searchAtlasUsers').then(result => {
            this.$set(this.$Session, 'userId', result.id);
            this.$Session.alerts = result.alerts.items;
          }).catch(error => {
              this.$c.log("error while getting user", error);
          });
          this.$c.log("main.js beforeCreate this.$Amplify.Auth.currentAuthenticatedUser() success!", this.$Session.user);
          this.getUserTeamList(this.$Session.user.username, 'searchTeamUsers').then(result => {
            this.handleTeamListResponse(result);
          }).catch(error => {
            this.handleTeamListError(error);
          });
          vm.$mount("#app");
        }
      })
      .catch(e => { 
        if(this.checkPath(this.$router.currentRoute.fullPath)){
          this.$router.push({ path: '/login', query: Object.assign(this.$route.query, { redirect: this.$router.currentRoute.fullPath }) })
        }               
        this.$c.log("main.js - this.$Amplify.Auth.currentAuthenticatedUser() failure", e);

        vm.$mount("#app");
      });
  },
  methods: {
    signOut() {
      this.$Amplify.Auth.signOut();
      this.$set(this.$Session, 'userId', '');
      this.$set(this.$Session, 'alerts', []);
      this.$set(this.$Session, 'groups', []);
      this.$set(this.$Session, 'isInternalGroupMember', false);
      this.$set(this.$Session, 'isAdminGroupMember', false);
      this.$router.push("/login");
    },
    async getCurrentUser(Id, queryId) {
      return new Promise(async (resolve, reject) => {
        let params = {}, operation = [];
        params.filter = {"cogUsername": {"match": Id }};
        params.alertsFilter = { status: { eq: 'Unread' } };
        // get recent notifications - we show the count and display 5 
        // latest notifications in the toolbar
        params.alertsLimit = 999;
        params.alertsSortDirection = 'DESC';
  
        operation = this.$Amplify.graphqlOperation(queries_custom[queryId], params);
        await this.$Amplify.API.graphql(operation)
        .then(result => {
          console.log("getCurrentUser",result)
          if (
            result.data && 
            result.data.searchUsers && 
            result.data.searchUsers.items && 
            result.data.searchUsers.items[0]
          ) {
            resolve(result.data.searchUsers.items[0]);
          }
          else 
            reject (new Error('Cognito Userid '+ Id +'not found in user table'));
        })
        .catch(error => {
          reject (error); 
        });
      })
    },
    async getUserTeamList(Id, queryId) {
      return new Promise(async (resolve, reject) => {
        let params = {}, operation = [];
        params.filter = {"cognito_userid": {"eq": Id }};
  
        operation = this.$Amplify.graphqlOperation(queries[queryId], params);
        await this.$Amplify.API.graphql(operation)
        .then(res => {
          let userTeamList = res.data.searchTeamUsers.items;
          let groupArr = [];
  
          userTeamList.forEach(element => {
            groupArr.push({
              teamId: element.team.id,
              name: element.team.name,
              role: element.role
            });
          });
          console.log("groupArr ", groupArr)
         resolve(groupArr);
        })
        .catch(error => {
          reject (error); 
        });
      })
    },
    isInternalGroupMember() {
      if (!this.$Session) {
        return false;
      }
      if (this.$reduce(this.$Session, 'user.attributes.custom:internal')) {
        return true;
      }
      if (this.$Session.groups && this.$Session.groups.length) {
        const index = this.$Session.groups.findIndex(_group => {
          return 'internal only' == _group.name.toLowerCase();
        });
        if (-1 != index) {
          return true;
        }
      }
      const user = this.$Session.user;
      if (
        !user || 
        !user.signInUserSession || 
        !user.signInUserSession.idToken || 
        !user.signInUserSession.idToken.payload ||
        !user.signInUserSession.idToken.payload['cognito:groups']
      ) {
        return false;
      }
      const index = user.signInUserSession.idToken.payload['cognito:groups'].findIndex(_group => {
        return 'internal_only' == _group.toLowerCase();
      });
      return -1 != index;
    },
    /**
     * Does the current logged in user belongs to the Admin group
     * @returns {boolean}
     */
    isAdminGroupMember() {
      if (!this.$Session || !this.$Session.groups || !this.$Session.groups.length) {
        return false;
      }
      const index = this.$Session.groups.findIndex(_group => {
        return _group && _group.name && 'admin' == _group.name.toLowerCase();
      });
      return -1 != index;
    },
    /**
     * Does the current logged in user has TaskSaveAsTemplate role
     * @returns {boolean}
     */
    hasTaskSaveAsTemplate() {
      return this.$reduce(this.$Session, 'user.attributes.custom:TaskSaveAsTemplate');
    },
    /**
     * Does the current logged in user has CaseSaveAsTemplate role
     * @returns {boolean}
     */
    hasCaseSaveAsTemplate() {
      return this.$reduce(this.$Session, 'user.attributes.custom:CaseSaveAsTemplate');
    },
    /**
     * Handle team list response - setup session data for global access
     * @param {object} result - holds current user team info
     * @returns {void}
     */
    handleTeamListResponse(result) {
      this.$set(this.$Session, 'groups', result);
      this.$c.log("this.$Session.groups", this.$Session.groups);
      this.$set(this.$Session, 'isInternalGroupMember', this.isInternalGroupMember());
      this.$set(this.$Session, 'isAdminGroupMember', this.isAdminGroupMember());
      this.$set(this.$Session, 'hasCaseSaveAsTemplate', this.hasCaseSaveAsTemplate());
      this.$set(this.$Session, 'hasTaskSaveAsTemplate', this.hasTaskSaveAsTemplate());
    },
    /**
     * Handle team list error response - the error response may still hold 
     * data - if it does - then process and set session variables accordingly
     * @param {object} error - the error response object
     * @returns {void} 
     */
    handleTeamListError(error) {
      this.$c.log("error while get teams role", error);
      if (error && error.data && error.data.searchTeamUsers && error.data.searchTeamUsers.items) {
        const teams = [];
        error.data.searchTeamUsers.items.map(_item => {
          if (_item && _item.id && _item.team) {
            teams.push({ teamId: _item.team.id, name: _item.team.name, role: _item.role });
          }
        });
        this.handleTeamListResponse(teams);
      }
    },
    /**
     * Watch for session change - if the current route is for admin users only
     * and the current logged in user is not an admin then redirect 
     * to the dashboard page
     * We wait for the session to get ready before we authorize
     */
    redirectIfNotAdmin() {
      if (
        undefined === this.$Session.isAdminGroupMember ||
        !this.$route ||
        !this.$route.meta ||
        true !== this.$route.meta.isAdminOnly
      ) {
        return;
      }
      if (true !== this.$Session.isAdminGroupMember) {
        this.$router.push('/');
      }
    },
    checkPath(path) {
      if(path == "/register" || path == "/forgotPassword"){
        return false
      }
        return true
    }
  },
  watch: {
    '$Session.isAdminGroupMember': {
      immediate: true,
      handler: function() {
        this.redirectIfNotAdmin();
      }
    },
    '$route': {
      immediate: true,
      handler: function(to, from) {
        this.redirectIfNotAdmin();
      }
    },
  }
});

if (typeof ga !== "undefined" && window.location.hostname !== 'localhost') {
  ga('set', 'page', router.currentRoute.path);
  ga('send', 'pageview');
  console.log('ga sent pageview [' + router.currentRoute.path + ']');

  router.afterEach(( to, from ) => {
    ga('set', 'page', to.path);
    ga('send', 'pageview');
    console.log('ga sent pageview [' + to.path + ']');
  });
} else {
  console.error('GA UNDEFINED')
}

window.$bus = new Vue();
